import api from '../api/axios';
import { InstanciaPaso, PeriodoProceso } from '../types/instanciaPaso';
import { OpcionPregunta, TipoPregunta } from '../types/paso';

export const instanciaPasoService = {
  /** Mis procesos como estudiante: el historial propio, que es corto. */
  mios: async (): Promise<InstanciaPaso[]> => {
    const { data } = await api.get<InstanciaPaso[]>('instancias/', { params: { vista: 'mias' } });
    return data;
  },

  /**
   * Procesos que guío, coguío o evalúo (todos, si soy coordinador).
   *
   * Se pide un semestre a la vez: sin `periodo` vienen los abiertos —lo que la
   * pantalla muestra por defecto—, y con período, ese semestre completo. El
   * coordinador tiene miles de procesos históricos y traerlos todos para pintar
   * cincuenta era lo que dejaba la vista colgada.
   */
  staff: async (periodo: string): Promise<InstanciaPaso[]> => {
    const { data } = await api.get<InstanciaPaso[]>('instancias/', {
      params: periodo ? { vista: 'staff', periodo } : { vista: 'staff' },
    });
    return data;
  },

  /** Semestres con procesos míos (llena el selector, que ya no puede deducirse de la lista). */
  periodos: async (): Promise<PeriodoProceso[]> => {
    const { data } = await api.get<PeriodoProceso[]>('instancias/periodos/');
    return data;
  },

  get: async (id: number): Promise<InstanciaPaso> => {
    const response = await api.get<InstanciaPaso>(`instancias/${id}/`);
    return response.data;
  },

  /** El estudiante sube/actualiza el documento (PDF) de su proceso. */
  subirDocumento: async (id: number, archivo: File): Promise<InstanciaPaso> => {
    const fd = new FormData();
    fd.append('documento', archivo);
    const { data } = await api.put<InstanciaPaso>(`instancias/${id}/documento/`, fd);
    return data;
  },

  /** El coordinador reasigna (temaId) o quita (null) el tema del proceso. */
  /** Pone o cambia el tema del proceso (título, guía, coguía) sin tocar el catálogo. */
  editarTema: async (id: number, input: TemaProcesoInput): Promise<InstanciaPaso> => {
    const { data } = await api.put<InstanciaPaso>(`instancias/${id}/tema/`, input);
    return data;
  },

  reasignarTema: async (id: number, temaId: number | null): Promise<InstanciaPaso> => {
    const { data } = await api.patch<InstanciaPaso>(`instancias/${id}/tema/`, { tema: temaId });
    return data;
  },

  /** Comisión de un proceso: actuales + sugeridos (coordinador). */
  getComision: async (id: number): Promise<ComisionData> => {
    const { data } = await api.get<ComisionData>(`instancias/${id}/comision/`);
    return data;
  },

  /** Confirma la comisión. Por id de persona: un externo puede no tener RUT. */
  confirmarComision: async (id: number, ids: number[]): Promise<InstanciaPaso> => {
    const { data } = await api.put<InstanciaPaso>(`instancias/${id}/comision/`, { ids });
    return data;
  },

  /** Formulario de evaluación + mi evaluación (integrante de la comisión). */
  getEvaluacion: async (id: number): Promise<EvaluacionForm> => {
    const { data } = await api.get<EvaluacionForm>(`instancias/${id}/evaluacion/`);
    return data;
  },

  /** Envía mi evaluación: resultado (nota o categoría) + preguntas de apoyo + comentarios. */
  enviarEvaluacion: async (id: number, input: EnviarEvaluacionInput): Promise<InstanciaPaso> => {
    const { data } = await api.post<InstanciaPaso>(`instancias/${id}/evaluacion/`, input);
    return data;
  },

  /** Coordinador: publica la nota (cierra el proceso con el promedio de la comisión). */
  /** El coordinador corrige el estado a mano (volver a evaluación, dar de baja…). */
  cambiarEstado: async (id: number, estado: string): Promise<InstanciaPaso> => {
    const { data } = await api.patch<InstanciaPaso>(`instancias/${id}/estado/`, { estado });
    return data;
  },

  /**
   * Publica el resultado. `anticipada` es publicar sin esperar a toda la comisión;
   * `resultado` es el desenlace elegido (aprobado, observacion o reprobado).
   */
  publicarNota: async (id: number, anticipada = false, resultado?: string): Promise<InstanciaPaso> => {
    const { data } = await api.post<InstanciaPaso>(`instancias/${id}/publicar-nota/`, { anticipada, resultado });
    return data;
  },
};

export interface ComisionData {
  tamano: number;
  /** `evaluo`: ya respondió; sacarlo guarda su evaluación pero deja de contarla. */
  actuales: { id: number; rut: number | null; nombre: string; evaluo: boolean }[];
  sugeridos: { id: number; rut: number | null; nombre: string; carga: number }[];
  /** No pueden evaluar este trabajo (guía/coguía del proceso y del tema). */
  excluidos: number[];
}

/** El tema de un proceso: se escribe en el proceso, no en el catálogo. */
export interface TemaProcesoInput {
  titulo: string;
  guia: number;
  coguia: number | null;
  coguia_externo: string;
  coguia_externo_email: string;
  /** Enlace al tema del catálogo, cuando se trae de otro paso que lo tenía. */
  tema: number | null;
}

export interface EnviarEvaluacionInput {
  /** Todo lo que respondió el evaluador, contra los ids de las preguntas del paso.
   *  Una de esas preguntas define el resultado del proceso; no hay campos aparte. */
  respuestas: Record<string, unknown>;
}

export interface EvaluacionForm {
  instrucciones: string;
  preguntas: { id: number; texto: string; tipo: TipoPregunta; opciones: OpcionPregunta[] }[];
  /** Cuál de las preguntas define el resultado del proceso. */
  pregunta_resultado: number | null;
  mi_evaluacion: { respuestas: Record<string, unknown> } | null;
}
